/**
 * A trial balance's rows, its month's documents and its warnings (W3-17 (8d), D-94, D-95).
 *
 * Shown where a trial balance is approved (Upload Data's preview) and where its lines are
 * corrected (Raw Data). The service works out every figure and every hold and serves them with
 * the lines (`trial_balance` on Review's read); this panel says them, and takes a person's two
 * standing answers: which rows a sheet that names no ledger reports (kept on its layout, 8b), and
 * which taxable carve-outs are not carved from exempt income (8c). Each is saved, then the lines
 * are read again, so what the answer changed is on screen at once.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { type ReactNode, useState } from 'react'

import {
  ApiError,
  type TrialBalanceDocument,
  type TrialBalanceRow,
  type TrialBalanceScreen,
  columnMaps,
  trialBalanceMarks,
} from '../lib/api'
import { rupees } from '../lib/invoicePreview'
import {
  linkable,
  linksTo,
  markable,
  rateWords,
  refOf,
  remarkWords,
  reportsWords,
} from '../lib/trialBalance'

interface Props {
  tb: TrialBalanceScreen
  clientId: string
  canEdit: boolean
  /** Read the lines again: a link or a mark changes what the rows come to. */
  onChanged: () => Promise<unknown> | void
}

export function TrialBalancePane({ tb, clientId, canEdit, onChanged }: Props) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The document whose link is open for editing, and the rows ticked in it, by row key. */
  const [linking, setLinking] = useState<{ doc: TrialBalanceDocument; keys: Set<string> } | null>(
    null,
  )

  const save = async (fn: () => Promise<unknown>) => {
    setSaving(true)
    setError(null)
    try {
      await fn()
      setLinking(null)
      await onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That was not saved.')
    } finally {
      setSaving(false)
    }
  }

  const ledgers = tb.rows.filter((r) => !r.carve_out)
  const carveOuts = tb.rows.filter((r) => r.carve_out)

  const openLink = (doc: TrialBalanceDocument) =>
    setLinking({
      doc,
      keys: new Set(tb.rows.filter((r) => linksTo(doc, r)).map((r) => r.key)),
    })

  const toggle = (key: string) =>
    setLinking((current) => {
      if (!current) return current
      const keys = new Set(current.keys)
      if (keys.has(key)) keys.delete(key)
      else keys.add(key)
      return { ...current, keys }
    })

  const saveLink = () => {
    if (!linking?.doc.profile_id) return
    const rows = tb.rows.filter((r) => linking.keys.has(r.key)).map(refOf)
    const mapId = linking.doc.profile_id
    void save(() =>
      rows.length ? columnMaps.linkLedger(mapId, rows) : columnMaps.unlinkLedger(mapId),
    )
  }

  return (
    <Card className="space-y-4 p-4">
      <div>
        <p className="gst-panel-h">The trial balance</p>
        <p className="gst-meta">
          Sheet “{tb.sheet}”{tb.month_heading && <>, column {tb.month_heading}</>}. Each income
          row is its month less what the month’s documents report of it; a row that holds says why.
          Approving the trial balance is reading its notes: a noted row is booked once approved, and
          a line you disagree with is taken out on Raw Data.
        </p>
      </div>

      {error && <Alert tone="error">{error}</Alert>}
      {tb.warnings.map((w) => (
        <Alert key={w} tone="warning">
          {w}
        </Alert>
      ))}

      <RowTable title="Ledgers" rows={ledgers} holdWords={tb.hold_words} />
      {carveOuts.length > 0 && (
        <RowTable
          title="Carve-outs: taxable and exempt services inside the income above"
          rows={carveOuts}
          holdWords={tb.hold_words}
          mark={
            canEdit
              ? (row) => (
                  <label className="mt-1 flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={row.marked}
                      disabled={saving}
                      onChange={() =>
                        void save(() =>
                          row.marked
                            ? trialBalanceMarks.unmark(clientId, row.name)
                            : trialBalanceMarks.mark(clientId, row.name),
                        )
                      }
                    />
                    Not carved from exempt income
                  </label>
                )
              : undefined
          }
        />
      )}

      <div>
        <p className="gst-panel-h mb-2">The month’s other sales documents</p>
        {tb.documents.length === 0 ? (
          <p className="gst-meta">None yet: every row is taken whole.</p>
        ) : (
          <ul className="space-y-3">
            {tb.documents.map((doc) => (
              <li key={doc.document_id ?? doc.label} className="text-sm">
                <p>
                  <b>{doc.label}</b>
                  {doc.awaits_approval && <span className="gst-tag ml-2">awaits approval</span>}
                </p>
                <p className="gst-meta">{reportsWords(doc)}</p>
                {canEdit && linkable(doc) && linking?.doc !== doc && (
                  <div className="mt-1 flex gap-2">
                    <Button size="sm" variant="outline" disabled={saving} onClick={() => openLink(doc)}>
                      {doc.reports === 'linked' ? 'Change its rows' : 'Link it to its rows'}
                    </Button>
                    {doc.reports === 'linked' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={saving}
                        onClick={() =>
                          void save(() => columnMaps.unlinkLedger(doc.profile_id as string))
                        }
                      >
                        Remove the link
                      </Button>
                    )}
                  </div>
                )}
                {linking?.doc === doc && (
                  <fieldset className="mt-2 rounded border p-2">
                    <legend className="gst-meta px-1">
                      The rows “{doc.label}” reports. Kept for the client: next month’s sheet is
                      linked the same way.
                    </legend>
                    <div className="max-h-64 space-y-1 overflow-auto">
                      {tb.rows.map((row) => (
                        <label key={row.key} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={linking.keys.has(row.key)}
                            onChange={() => toggle(row.key)}
                          />
                          {row.code && <span className="gst-mono">{row.code}</span>}
                          <span>{row.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" disabled={saving} onClick={saveLink}>
                        Save the link
                      </Button>
                      <Button size="sm" variant="outline" disabled={saving} onClick={() => setLinking(null)}>
                        Cancel
                      </Button>
                    </div>
                  </fieldset>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  )
}

function RowTable({
  title,
  rows,
  holdWords,
  mark,
}: {
  title: string
  rows: TrialBalanceRow[]
  holdWords: Record<string, string>
  mark?: (row: TrialBalanceRow) => ReactNode
}) {
  if (rows.length === 0) return null
  return (
    <div>
      <p className="gst-panel-h mb-2">{title}</p>
      <div className="gst-table-wrap">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">Code</th>
              <th className="is-text">Ledger</th>
              <th className="is-text">GST remark</th>
              <th className="is-text">Rate</th>
              <th>Month (₹)</th>
              <th>Reported (₹)</th>
              <th className="is-text">This month</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td className="is-text gst-mono">{row.code ?? '—'}</td>
                <td className="is-text">{row.name}</td>
                <td className="is-text">{remarkWords(row.remark)}</td>
                <td className="is-text">{rateWords(row)}</td>
                <td>{rupees(row.month)}</td>
                <td title={row.by.join(', ') || undefined}>{rupees(row.reported)}</td>
                <td className="is-text">
                  <p>{row.words}</p>
                  {row.holds.map((h) => (
                    <p key={h}>
                      <span className="gst-chip is-hold">Holds</span>{' '}
                      <span className="text-xs">{holdWords[h] ?? h}</span>
                    </p>
                  ))}
                  {mark && markable(row) && mark(row)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
